<template>
  <div class="station-location-widget">
    <div class="toolbar">
      <input
        v-model="searchQuery"
        type="text"
        placeholder="Standort suchen"
        :disabled="disabled || loading"
        @keyup.enter="searchLocation"
      />

      <button
        type="button"
        @click="searchLocation"
        :disabled="disabled || loading || !searchQuery.trim()"
      >
        {{ loading ? "Suche…" : "Suchen" }}
      </button>

      <!-- <button type="button" @click="locateMe" :disabled="disabled || loading">
        Mein Standort
      </button> -->
    </div>

    <div ref="map" class="map"></div>

    <!-- <div v-if="selected" class="result">
      <div><strong>Breitengrad:</strong> {{ selected.lat }}</div>
      <div><strong>Längengrad:</strong> {{ selected.lng }}</div>
      <div><strong>Adresse:</strong> {{ selected.address || "—" }}</div>
    </div> -->
  </div>
</template>

<script>
import L from "leaflet";
import "leaflet/dist/leaflet.css";

export default {
  name: "LocationMapPicker",

  props: {
    value: {
      type: Object,
      default: function () {
        return {
          lat: null,
          lng: null,
          address: "",
        };
      },
    },

    disabled: {
      type: Boolean,
      default: false,
    },

    zoom: {
      type: Number,
      default: 13,
    },

    height: {
      type: String,
      default: "420px",
    },
  },

  data() {
    return {
      map: null,
      marker: null,
      loading: false,
      searchQuery: "",
      isApplyingExternalValue: false,
      selected: {
        lat: this.isValidNumber(this.value && this.value.lat) ? this.value.lat : 52.52,
        lng: this.isValidNumber(this.value && this.value.lng) ? this.value.lng : 13.405,
        address: this.value && this.value.address ? this.value.address : "",
        addressDetails: null,
      },
    };
  },

  watch: {
    value: {
      deep: true,
      handler(newValue) {
        if (!newValue) return;

        const hasLat = this.isValidNumber(newValue.lat);
        const hasLng = this.isValidNumber(newValue.lng);

        if (!hasLat || !hasLng) return;

        const nextLat = Number(Number(newValue.lat).toFixed(6));
        const nextLng = Number(Number(newValue.lng).toFixed(6));
        const nextAddress = newValue.address || "";

        const sameLat = this.selected.lat === nextLat;
        const sameLng = this.selected.lng === nextLng;
        const sameAddress = this.selected.address === nextAddress;

        if (sameLat && sameLng && sameAddress) return;

        this.isApplyingExternalValue = true;

        this.selected.lat = nextLat;
        this.selected.lng = nextLng;
        this.selected.address = nextAddress;

        if (this.marker) {
          this.marker.setLatLng([nextLat, nextLng]);
        }

        if (this.map) {
          this.map.setView([nextLat, nextLng], this.map.getZoom(), {
            animate: false,
          });
        }

        this.$nextTick(() => {
          this.isApplyingExternalValue = false;
        });
      },
    },

    disabled() {
      this.applyDisabledState();
    },
  },

  mounted() {
    this.initMap();
  },

  beforeDestroy() {
    if (this.map) {
      this.map.remove();
    }
  },

  methods: {
    isValidNumber(value) {
      return typeof value === "number" && !Number.isNaN(value);
    },

    initMap() {
      this.map = L.map(this.$refs.map, {
        zoomControl: true,
        attributionControl: true,
      }).setView([this.selected.lat, this.selected.lng], this.zoom);

      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        maxZoom: 19,
        attribution: "&copy; OpenStreetMap contributors",
      }).addTo(this.map);

      const markerIcon = L.divIcon({
        className: "custom-map-pin-wrapper",
        html: '<div class="custom-map-pin"></div>',
        iconSize: [24, 24],
        iconAnchor: [12, 24],
      });

      this.marker = L.marker([this.selected.lat, this.selected.lng], {
        draggable: !this.disabled,
        icon: markerIcon,
      }).addTo(this.map);

      this.marker.on("dragend", () => {
        if (this.disabled) return;
        const pos = this.marker.getLatLng();
        this.setLocation(pos.lat, pos.lng, true);
      });

      this.map.on("click", (e) => {
        if (this.disabled) return;
        this.setLocation(e.latlng.lat, e.latlng.lng, true);
      });

      this.$refs.map.style.height = this.height;
      this.map.invalidateSize();

      this.applyDisabledState();

      if (!this.selected.address) {
        this.reverseGeocode(this.selected.lat, this.selected.lng);
      }
    },

    applyDisabledState() {
      if (!this.map || !this.marker) return;

      if (this.disabled) {
        this.map.dragging.disable();
        this.map.scrollWheelZoom.disable();
        this.map.doubleClickZoom.disable();
        this.map.boxZoom.disable();
        this.map.keyboard.disable();
        this.map.touchZoom.disable();

        if (this.marker.dragging) {
          this.marker.dragging.disable();
        }
      } else {
        this.map.dragging.enable();
        this.map.scrollWheelZoom.enable();
        this.map.doubleClickZoom.enable();
        this.map.boxZoom.enable();
        this.map.keyboard.enable();
        this.map.touchZoom.enable();

        if (this.marker.dragging) {
          this.marker.dragging.enable();
        }
      }
    },

    async setLocation(lat, lng, resolveAddress) {
      this.selected.lat = Number(Number(lat).toFixed(6));
      this.selected.lng = Number(Number(lng).toFixed(6));

      if (this.marker) {
        this.marker.setLatLng([this.selected.lat, this.selected.lng]);
      }

      if (resolveAddress) {
        await this.reverseGeocode(this.selected.lat, this.selected.lng);
      } else {
        this.emitChange();
      }
    },

    async searchLocation() {
      const query = this.searchQuery.trim();
      if (!query || this.disabled) return;

      this.loading = true;

      try {
        const url =
          "https://global.codeguides.net/search?" +
          new URLSearchParams({
            q: query,
            format: "jsonv2",
            limit: "1",
            addressdetails: "1",
            // countrycodes: "de",
          }).toString();

        const response = await fetch(url, {
          headers: {
            Accept: "application/json",
            "Accept-Language": "de",
          },
        });

        if (!response.ok) {
          throw new Error("Standortsuche fehlgeschlagen");
        }

        const results = await response.json();

        if (!Array.isArray(results) || !results.length) {
          throw new Error("Kein Treffer gefunden");
        }

        const hit = results[0];
        const lat = parseFloat(hit.lat);
        const lng = parseFloat(hit.lon);

        this.selected.lat = Number(lat.toFixed(6));
        this.selected.lng = Number(lng.toFixed(6));
        this.selected.address = hit.display_name || "";
        this.selected.addressDetails = hit.address || null;

        if (this.marker) {
          this.marker.setLatLng([this.selected.lat, this.selected.lng]);
        }

        if (this.map) {
          this.map.setView([this.selected.lat, this.selected.lng], 16);
        }

        this.emitChange();
      } catch (error) {
        this.$emit("error", error);
      } finally {
        this.loading = false;
      }
    },

    async reverseGeocode(lat, lng) {
      this.loading = true;

      try {
        const url =
          "https://global.codeguides.net/reverse?" +
          new URLSearchParams({
            lat: String(lat),
            lon: String(lng),
            format: "jsonv2",
            addressdetails: "1",
            zoom: "18",
          }).toString();

        const response = await fetch(url, {
          headers: {
            Accept: "application/json",
            "Accept-Language": "de",
          },
        });

        if (!response.ok) {
          throw new Error("Reverse-Geocoding fehlgeschlagen");
        }

        const result = await response.json();

        this.selected.address = result && result.display_name ? result.display_name : "";
        this.selected.addressDetails = result && result.address ? result.address : null;

        this.emitChange();
      } catch (error) {
        this.$emit("error", error);
      } finally {
        this.loading = false;
      }
    },

    // locateMe() {
    //   if (this.disabled) return;

    //   if (!navigator.geolocation) {
    //     this.$emit("error", new Error("Geolocation wird vom Browser nicht unterstützt"));
    //     return;
    //   }

    //   this.loading = true;

    //   navigator.geolocation.getCurrentPosition(
    //     async (position) => {
    //       const lat = position.coords.latitude;
    //       const lng = position.coords.longitude;

    //       if (this.map) {
    //         this.map.setView([lat, lng], 16);
    //       }

    //       await this.setLocation(lat, lng, true);
    //       this.loading = false;
    //     },
    //     (error) => {
    //       this.loading = false;
    //       this.$emit("error", error);
    //     },
    //     {
    //       enableHighAccuracy: true,
    //       timeout: 10000,
    //       maximumAge: 0,
    //     }
    //   );
    // },

    emitChange() {
      if (this.isApplyingExternalValue) return;

      const payload = {
        lat: this.selected.lat,
        lng: this.selected.lng,
        address: this.selected.address,
        addressDetails: this.selected.addressDetails,
      };

      this.$emit("input", payload);
      this.$emit("change", payload);
    },
  },
};
</script>

<style scoped>
.station-location-widget {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 16px;
}

.toolbar {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.toolbar input {
  flex: 1 1 260px;
  min-width: 220px;
  padding: 8px 10px;
  border: 1px solid #cfcfcf;
  border-radius: 4px;
  outline: none;
}

.toolbar button {
  padding: 8px 12px;
  border: 1px solid #cfcfcf;
  background: white;
  border-radius: 4px;
  cursor: pointer;
}

.toolbar button:disabled,
.toolbar input:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.map {
  width: 100%;
  height: 420px;
  border: 1px solid #dcdcdc;
  border-radius: 6px;
  overflow: hidden;
}

.result {
  padding: 12px;
  background: #f7f7f7;
  border: 1px solid #e5e5e5;
  border-radius: 6px;
  line-height: 1.5;
}

::v-deep .custom-map-pin-wrapper {
  background: transparent;
  border: none;
}

::v-deep .custom-map-pin {
  width: 18px;
  height: 18px;
  background: #1976d2;
  border: 3px solid #fff;
  border-radius: 50% 50% 50% 0;
  transform: rotate(-45deg);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.35);
}

::v-deep .custom-map-pin::after {
  content: "";
  width: 6px;
  height: 6px;
  background: #fff;
  position: absolute;
  border-radius: 50%;
  top: 3px;
  left: 3px;
}
</style>